import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { useAuthenticatedUser } from '../../../../auth/hooks/useAuthenticatedUser'
import { Grid } from '../../../../theme/components/Grid'
import { useWerklijsten } from '../../hooks/useWerklijsten'
import type { OnClickTile } from '../../types/DashboardNavigation'
import type { Werklijst } from '../../types/Werklijst'

import { WerklijstTile } from './WerklijstTile'
import { WerklijstenFilters } from './WerklijstenFilters'
import {
  generateActiesQuery,
  generateActiviteitenQuery,
  generateAssignedBijlagenQuery,
} from './werklijst-util'

interface Props {
  filterOptions: {
    gebieden: Gebied[]
    actiehouders: GroupedFilterOption[]
    labels: FilterOption[]
  }
  onClickTile: OnClickTile
}

export const Werklijsten = ({ onClickTile, filterOptions }: Props) => {
  const {
    data: { acties, bijlagen, activiteiten },
    filters,
    initialFilters,
    onChangeFilters,
    onRefresh,
    onReset,
  } = useWerklijsten()

  const user = useAuthenticatedUser()

  const onClickTileActies = (werklijst: Werklijst) => {
    onClickTile(generateActiesQuery({ werklijst, filters, user }))
  }

  const onClickTileBijlagen = (werklijst: Werklijst, kpiColor?: string) => {
    onClickTile(generateAssignedBijlagenQuery({ werklijst, filters, kpiColor }))
  }

  const onClickTileActiviteiten = (werklijst: Werklijst, kpiColor?: string) => {
    onClickTile(generateActiviteitenQuery({ werklijst, filters, kpiColor }))
  }

  return (
    <>
      <WerklijstenFilters
        onChange={onChangeFilters}
        onRefresh={onRefresh}
        onReset={onReset}
        filters={filters}
        initialFilters={initialFilters}
        filterOptions={filterOptions}
      />

      <Box sx={{ p: '50px', ml: '20px' }}>
        <Accordion sx={{ width: '100%' }} defaultExpanded key="acties">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: '24px', pl: '24px' }}>
              Acties
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              {acties.map((werklijst) => (
                <Grid key={werklijst.label}>
                  <WerklijstTile
                    werklijst={werklijst}
                    onClickTile={onClickTileActies}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ width: '100%' }} defaultExpanded key="bijlagen">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: '24px', pl: '24px' }}>
              Bijlagen
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              {bijlagen.map((werklijst) => (
                <Grid key={werklijst.label}>
                  <WerklijstTile
                    werklijst={werklijst}
                    onClickTile={onClickTileBijlagen}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ width: '100%' }} defaultExpanded key="Activiteiten">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: '24px', pl: '24px' }}>
              Activiteiten
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              {activiteiten.map((werklijst) => (
                <Grid key={werklijst.label}>
                  <WerklijstTile
                    werklijst={werklijst}
                    onClickTile={onClickTileActiviteiten}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  )
}
